<template>
  <CardFlat
    hasInfoIcon
    :iconPosition="isIs24 ? 'right' : 'static'"
    @onInfoIconClick="isModalVisible = true"
  >
    <template #title>{{ t('priceRoomDistribution.heading') }}</template>
    <template #description>
      {{ caption }}
    </template>
    <div v-if="propertyData && propertyData.length > 0">
      <div v-if="!isIs24">
        <HgSelect
          v-model="roomNumberStr"
          :options="selectOptionsNorm"
          name="select"
          :class="$style.selectFilter"
          data-testid="selectFilter"
          @update:modelValue="filterData"
        />
      </div>
      <HgSkeleton v-if="isLoading" height="168px" :class="$style.loaderGap" />
      <div v-else>
        <div
          v-if="roomDataFound"
          :class="[
            {
              [$style.subHeading]: isIs24,
              [$style.subHeadingHg]: !isIs24,
            },
          ]"
        >
          <p>{{ currency }}{{ numberFormatted(medianValue) }}</p>
        </div>
        <RoomDistributionImage :dataFound="roomDataFound" />
        <div
          v-if="roomDataFound"
          :class="[
            {
              [$style.chartFootValue]: isIs24,
              [$style.chartFootValueHg]: !isIs24,
            },
          ]"
        >
          <p
            :class="[
              {
                [$style.subHeadingFoot]: isIs24,
                [$style.subHeadingFootHg]: !isIs24,
              },
            ]"
          >
            {{ currency }}{{ numberFormatted(minValue) }}
          </p>
          <p
            :class="[
              {
                [$style.subHeadingFoot]: isIs24,
                [$style.subHeadingFootHg]: !isIs24,
              },
            ]"
          >
            {{ currency }}{{ numberFormatted(maxValue) }}
          </p>
        </div>
      </div>

      <div v-if="isIs24" :class="$style.is24FooterWrapper">
        <HgSegmentedButton
          v-model="roomNumber"
          :class="$style.segmentedButton"
          name="roomType"
          :options="segmentOptionsNorm"
          @update:modelValue="filterData"
        />
        <HgSkeleton v-if="isLoading" height="112px" :class="$style.loaderGap" />
        <div v-else>
          <p v-if="roomDataFound" :class="$style.summary" data-testid="summary">
            {{ summary }}
          </p>
          <p v-else :class="$style.noDataSummary">
            {{ t('priceRoomDistribution.insufficientData.title') }}
          </p>
        </div>
      </div>
      <p v-if="roomDataFound" :class="$style.dataTimePeriod">
        {{ t('priceRoomDistribution.dataTimePeriod', [startDate, endDate]) }}
      </p>
      <p v-else :class="$style.dataTimePeriod">
        {{ t('priceRoomDistribution.insufficientData.text') }}
      </p>
    </div>
    <div v-else :class="$style.noDataContainer">
      <span :class="$style.summary" data-testid="noData">{{ t('common.noData') }}</span>
    </div>
    <HgModal v-model="isModalVisible" mode="dialog" @close="isModalVisible = false">
      <template #title> {{ t('tooltips.pricesRelationRooms.title') }}</template>
      <div
        v-for="{ title, description } in modalContent"
        :key="title"
        :class="$style.tooltipContentWrapper"
      >
        <p :class="$style.tooltipContentHeading">{{ title }}</p>
        <p :class="$style.tooltipContentDescription">{{ description }}</p>
      </div>
    </HgModal>
  </CardFlat>
</template>
<script setup lang="ts">
import {
  HgModal,
  hgNumber,
  HgSegmentedButton,
  type HgSegmentedOptions,
  HgSelect,
  type HgSelectOptions,
  HgSkeleton,
  HgSupportedLanguage,
} from '@smg-real-estate/domus';
import { storeToRefs } from 'pinia';
import { computed, defineAsyncComponent, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { isPlatformIs24 } from '@/helpers/platform';
import { PropertyType } from '@/models/iazi/common/enums';
import { type PriceRoomDistributionDataItem } from '@/models/iazi/queries/getPriceRoomDistributionResponse';
import useGeoStore from '@/stores/useGeoStore';
import useMapStore from '@/stores/useMapStore';
import { MapGeoLevel } from '@/types';
import { dateFilter } from '@/utils/functions';

import { trackPriceTrendRoomSelection } from '@/tracking';
import { CardFlat } from '../common';

const isModalVisible = ref(false);
const mapStore = useMapStore();
const { data, propertyType, isLoading, currentLevel } = storeToRefs(mapStore);
const { getLocationSentence } = storeToRefs(useGeoStore());

const { t, locale } = useI18n();

/* variables initialized from api response */
const roomDataFound = ref<boolean>(true);
const medianValue = ref<number>(0);
const minValue = ref<number>(0);
const maxValue = ref<number>(0);
const roomNumber = ref<number | string>(0);
const roomNumberStr = ref<string>('0');
const currency = ref('CHF ');
const startDate = ref<string>('');
const endDate = ref<string>('');
const isIs24 = isPlatformIs24();
const RoomDistributionImage = defineAsyncComponent(() =>
  isIs24
    ? import('@/components/price-room-distribution/RoomDistributionImageComponent.vue')
    : import('@/components/price-room-distribution/RoomDistributionImageComponentHg.vue'),
);

const propertyData = computed<PriceRoomDistributionDataItem[] | null | undefined>(() => {
  return data.value.priceRoom?.results[propertyType.value];
});

const roomList = computed<Array<number> | null | undefined>(() => {
  if (propertyType.value === PropertyType.Apartment) {
    return data.value.priceRoom?.results.roomsListApartment;
  } else {
    return data.value.priceRoom?.results.roomsListHouse;
  }
});

const setTimePerioid = (element: PriceRoomDistributionDataItem) => {
  if (element.startDate && element.endDate) {
    startDate.value = dateFilter(element.startDate);
    endDate.value = dateFilter(element.endDate);
  }
};

const roomShort = computed<string>(() => {
  return t('priceRoomDistribution.room.singular').charAt(0) + '.';
});

const roomFullSingular = computed<string>(() => {
  return t('priceRoomDistribution.room.singular');
});
const roomFullPlural = computed<string>(() => {
  return t('priceRoomDistribution.room.plural');
});

const segmentOptionsNorm = computed<HgSegmentedOptions[]>(() => {
  const returnValue: Array<HgSegmentedOptions> = [];
  for (let val of roomList.value ?? []) {
    const tempObj: HgSegmentedOptions = {
      value: val,
      text: val.toString() + ' ' + roomShort.value,
    };
    returnValue.push(tempObj);
  }
  return returnValue;
});

const selectOptionsNorm = computed<HgSelectOptions[]>(() => {
  const returnValue: Array<HgSelectOptions> = [];
  for (let val of roomList.value ?? []) {
    const room = val > 1 ? roomFullPlural.value : roomFullSingular.value;
    const tempObj: HgSelectOptions = {
      value: val.toString(),
      text: val.toString() + ' ' + room,
    };
    returnValue.push(tempObj);
  }
  return returnValue;
});

const numberFormatted = (value: number): string => {
  return hgNumber(value, locale.value as HgSupportedLanguage);
};

const filterData = (value: number | string): void => {
  const val = typeof value === 'string' ? parseFloat(value) : value;
  roomDataFound.value = false;
  for (let tempObj of propertyData.value ?? []) {
    if (tempObj.roomNb === val) {
      minValue.value = tempObj.priceRangeMin ?? 0;
      maxValue.value = tempObj.priceRangeMax ?? 0;
      medianValue.value = tempObj.priceValueMedian ?? 0;
      setTimePerioid(tempObj);
      roomDataFound.value = true;
      break;
    }
  }
  trackPriceTrendRoomSelection(val);
};

const setDefaults = () => {
  if (segmentOptionsNorm.value.length > 0) {
    roomNumber.value = segmentOptionsNorm.value[0].value;
  }
  if (selectOptionsNorm.value.length > 0) {
    roomNumberStr.value = selectOptionsNorm.value[0].value;
  }
};

function initData() {
  if (
    data.value.priceRoom?.results !== null &&
    data.value.priceRoom?.results !== undefined &&
    typeof data.value.priceRoom.results == 'object'
  ) {
    /* initializing segmentedbutton and select filter modelValue*/
    setDefaults();
    /* initializing min, max and median data */
    roomDataFound.value = false;
    for (let propertyObj of propertyData.value ?? []) {
      // eslint-disable-next-line max-depth
      if (
        propertyObj.roomNb == roomNumber.value ||
        propertyObj.roomNb == parseFloat(roomNumberStr.value)
      ) {
        minValue.value = propertyObj.priceRangeMin ?? 0;
        maxValue.value = propertyObj.priceRangeMax ?? 0;
        medianValue.value = propertyObj.priceValueMedian ?? 0;
        setTimePerioid(propertyObj);
        roomDataFound.value = true;
        break;
      }
    }
  }
}

const modalContent = computed<Array<{ title: string; description: string }>>(() => [
  {
    title: t('tooltips.dataUpdatePriceTrendsTitle'),
    description: t('tooltips.dataUpdatePriceTrends'),
  },
  {
    title: t('tooltips.marketVsOfferedTitle'),
    description: t('tooltips.marketVsOffered'),
  },
  {
    title: t('tooltips.medianPriceTitle'),
    description: t('tooltips.medianPrice'),
  },
]);

const generateSentence = (
  key: string,
  textkeyCb: (defaultKey: string) => string,
  additionalParams: any[] = [],
) => {
  const { value: getLocationSentenceFunc } = getLocationSentence;
  const { value: selectedLocale } = locale;
  const { value: selectedPropType } = propertyType;
  const locationSentence = getLocationSentenceFunc(t, selectedLocale as HgSupportedLanguage, {
    includePreposition: false,
  });

  const noOfRooms = isIs24 ? roomNumber.value : roomNumberStr.value;
  const wordForm = +noOfRooms > 1 ? 'plural' : 'singular';
  const textkey = textkeyCb(`priceRoomDistribution.${key}.${selectedPropType}.${wordForm}`);
  return t(textkey, ['', noOfRooms, locationSentence, ...additionalParams]) as string;
};

const caption = computed<string>(() =>
  generateSentence('caption', (defaultKey: string) => defaultKey),
);

const summary = computed<string>(() => {
  const appropriateLevel = pickAppropriateTextKeyByLevel(currentLevel.value);
  return generateSentence('summary', (defaultKey: string) => `${defaultKey}.${appropriateLevel}`, [
    currency.value,
    numberFormatted(medianValue.value),
    numberFormatted(minValue.value),
    numberFormatted(maxValue.value),
  ]);
});

const pickAppropriateTextKeyByLevel = (level: MapGeoLevel): string => {
  switch (level) {
    case MapGeoLevel.Canton:
      return 'canton';
    case MapGeoLevel.Town:
    case MapGeoLevel.Municipality:
      return 'muniandPost';
    case MapGeoLevel.Country:
    default:
      return 'Switzerland';
  }
};

// TODO: watch is really flexible, it's unsafe for operation and not benefit the performance
// TODO: move all related data fetching to top layer, and manage them together
watch(
  data,
  () => {
    initData();
  },
  { immediate: true },
);

watch(propertyType, () => {
  initData();
});
</script>

<style lang="scss" module>
@use '@/styles/variables' as *;

.subHeading {
  align-self: center;
  margin-top: $space-250;
  margin-bottom: $space-150;
  text-align: center;

  @include body-2;
}

.subHeadingHg {
  align-self: center;
  text-align: center;

  @include body-1-bold;
}

.subHeadingFoot {
  margin-top: $space-75;
  margin-bottom: $space-200;
  text-align: center;

  @include body-2;
}

.subHeadingFootHg {
  margin-top: $space-75;
  margin-bottom: $space-200;
  text-align: center;

  @include body-3;
}

.summary {
  margin-top: 0;
  margin-bottom: $space-75;
  color: $color-grey-120;

  @include body-3;
}

.dataTimePeriod {
  display: flex;
  width: inherit;
  margin-top: 0;
  margin-bottom: 0;
  color: $color-grey-90;

  @include caption;
}

.chartFootValue {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 0 $space-150;
  margin-bottom: $space-25;
}

.chartFootValueHg {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}

.is24FooterWrapper {
  > div[class*='HgFormFieldWrapper_noLabel'] {
    padding-top: 0;
  }
}

.segmentedButton {
  margin-bottom: $space-150;

  [class*='HgSegmentedButton_widget'] {
    padding: 0 5px;
    background-color: $color-grey-40;

    &[class*='HgSegmentedButton_primary'] [class*='HgSegmentedButton_active'] {
      &:not([class*='HgSegmentedButton_disabled']) {
        color: $color-black;
        background-color: $color-white;
        border-radius: 6px;
      }
    }

    [class*='HgSegmentedButton_option'] {
      position: relative;
      padding-top: 2px;
      padding-bottom: 2px;
      margin: 5px 0;
      background-color: inherit;
      border: unset;

      @include caption($color-black);

      & {
        line-height: 1.5;
      }

      &:not([class*='HgSegmentedButton_active'])
        + :not([class*='HgSegmentedButton_active'])::before {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        width: 0.5px;
        content: '';
        background-color: $color-grey-80;
      }
    }
  }
}

.noDataContainer {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100% !important;
  height: 280px !important;
}

.selectFilter {
  max-width: 134px;
}

.tooltipContentWrapper {
  margin-bottom: $space-250;
}

.tooltipContentDescription {
  margin-top: 0;
  margin-bottom: 0;

  @include body-2;
}

.tooltipContentHeading {
  margin-top: 0;
  margin-bottom: 0;

  @include heading-5;
}

.noDataSummary {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  margin-bottom: $space-75;
  color: $color-grey-90;
  text-align: center;

  @include body-3;
}

.loaderGap {
  margin-top: $space-75;
  margin-bottom: $space-75;
}
</style>
